/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/jsx-no-useless-fragment */
import React, { Suspense, useMemo } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/legacy/image';
import { useRouter } from 'next/router';

import get from 'lodash/get';

// HOOKS
import { Trans, useTranslation } from 'next-i18next';
import useSellReduxServices from '@source/hooks/useSellReduxServices';
import useWindowResize from '@source/hooks/useWindowResize';
import useIsIOS from '@source/hooks/useIsIOS';

// CONSTANTS
import { SELL_CAR_HERO_BANNER, SELL_CAR_HERRO_BANNER_JP } from '@source/pages/SellCar/constants';

// TYPES
import { TLanguageKey, TRegionKey } from '@source/interface';

import {
  StyledContent,
  StyledRow,
  StyledLeftBanner,
  StyledSellCarForm,
  StyledTitle,
  StyledWrapper,
  StyledContainerWrapper,
} from '@source/pages/SellCar/components/Banner/SellBanner.style';

import SellCarRequestFormContainerSkeleton from '@source/pages/SellCar/containers/SellCarRequestFormWrapperContainer.skeleton';
import { SUPPORTED_COUNTRIES } from '@source/constants/config';
import BannerHeadline from './BannerHeadline';

// import SellCarRequestFormWrapperContainer from '@source/pages/SellCar/containers/SellCarRequestFormWrapperContainer';
const SellCarRequestFormWrapperContainer = dynamic(
  () => import('@source/pages/SellCar/containers/SellCarRequestFormWrapperContainer'),
  {
    suspense: true,
  },
);

type SellBannerProps = {
  country?: TRegionKey;
};

type BannerTitle = {
  text?: string;
  orangeText: string;
  sellYourCarAt?: string;
  ourBestPrice?: string;
  sellYourCar?: string;
  atOurBestPrice?: string;
  sellYour?: string;
  carAtOurBestPrice?: string;
};

const noDramaSellPageCountries = ['sg', 'my', 'hk'] as const;

const SellBanner = ({ country = 'sg' }: SellBannerProps) => {
  const { query } = useRouter();
  const { t, i18n } = useTranslation('sellCar');
  const currentLang = i18n.language as TLanguageKey;
  const noDrama = noDramaSellPageCountries.includes(country as typeof noDramaSellPageCountries[number]);
  // // Temporarily hide the CRB-444 feature.
  // const noDrama = false;

  const title: BannerTitle = noDrama
    ? {
        orangeText: `sellCar.banner.${country}.noDrama`,
        sellYourCarAt: `sellCar.banner.${country}.sellYourCarAt`,
        ourBestPrice: `sellCar.banner.${country}.ourBestPrice`,
        sellYourCar: `sellCar.banner.${country}.sellYourCar`,
        atOurBestPrice: `sellCar.banner.${country}.atOurBestPrice`,
        sellYour: `sellCar.banner.${country}.sellYour`,
        carAtOurBestPrice: `sellCar.banner.${country}.carAtOurBestPrice`,
      }
    : {
        text: `sellCar.banner.${country}.sellYourCarFor`,
        orangeText: `sellCar.banner.${country}.onlyTheBestPrice`,
      };
  const isNewDesign =
    country === 'my' &&
    [
      'PM_GG_MY_WEB_SELL_SEM_Sales_PTG_AlwaysOn_Generic_Br',
      '2024_PM_GG_MY_WEB_SELL_SEM_Sales_PTG_AlwaysOn_Generic_Ex',
    ].includes(query?.utm_campaign as string);

  const heroBannerByRegion: Record<TRegionKey, (...args: any[]) => any> = {
    sg: SELL_CAR_HERO_BANNER,
    th: SELL_CAR_HERO_BANNER,
    id: SELL_CAR_HERO_BANNER,
    my: SELL_CAR_HERO_BANNER,
    hk: SELL_CAR_HERO_BANNER,
    jp: SELL_CAR_HERRO_BANNER_JP,
  };

  const { titleKey, desc, ...bgImgProps } = heroBannerByRegion[country](country, isNewDesign, noDrama);

  // Use useWindowSize instead of useMobileView. Because useMobileView's returning false boolean instead of undefined
  const [width] = useWindowResize();
  const isAboveTablet = width && width > 991;
  const isBelowTablet = width && width <= 991;

  //
  const isIOS = useIsIOS();

  //
  const { sellState } = useSellReduxServices();
  const { formInfo } = sellState;
  const { expanded } = formInfo || {};

  const bgKeyPath = useMemo(() => {
    let bgKey = null;
    const supportedScreenSizes = [375, 576, 768, 992, 1280, 1920];

    for (let index = 0; index < supportedScreenSizes.length; index += 1) {
      const ele = supportedScreenSizes[index];

      if (!width) bgKey = 'bgImg375';

      if (width <= supportedScreenSizes[0]) {
        bgKey = 'bgImg375';
        break;
      }
      if (width >= supportedScreenSizes[supportedScreenSizes.length - 1]) {
        bgKey = 'bgImg1920';
        break;
      }

      const nextEle = supportedScreenSizes[index + 1];

      if (width >= ele && width < nextEle) {
        bgKey = `bgImg${ele}`;
      }
    }

    return `${bgKey}${expanded && country !== 'id' ? 'Expanded' : ''}`;
  }, [width, expanded]);

  const imageSrc = get(bgImgProps, bgKeyPath);
  const transformSrc = imageSrc.replace(
    'https://cdn.carro.co/',
    'https://cdn.carro.co/tr:n-carro_sell_page_form_background_image/',
  );
  const blurSrc = get(bgImgProps, `${bgKeyPath}Blur`);

  const isUseBannerHeadLine = useMemo(() => [SUPPORTED_COUNTRIES.JP.code].includes(country), [country]);

  const bgObjectPosition = useMemo(() => {
    if (noDrama) return 'center center';

    if ([SUPPORTED_COUNTRIES.JP.code].includes(country)) {
      return isBelowTablet ? 'top left' : 'center center';
    }

    return 'top left';
  }, [country, noDrama, isBelowTablet]);

  return (
    <>
      <StyledWrapper {...formInfo} country={country} isNewDesign={isNewDesign} noDrama={noDrama}>
        <Image
          alt="banner"
          src={transformSrc}
          layout="fill"
          objectFit="cover"
          objectPosition={bgObjectPosition}
          priority
          blurDataURL={blurSrc}
          placeholder="blur"
          unoptimized
        />
        <StyledContainerWrapper>
          <StyledContent country={country} noDrama={noDrama}>
            <StyledRow country={country}>
              <StyledLeftBanner {...formInfo} country={country} noDrama={noDrama} lang={currentLang}>
                {isUseBannerHeadLine ? (
                  <BannerHeadline region={country} title={titleKey} desc={desc} />
                ) : (
                  <StyledTitle lang={currentLang} country={country} isIOS={isIOS}>
                    {noDrama ? (
                      <Trans
                        ns="sellCar"
                        i18nKey="sellCar.banner.dramaTitle"
                        components={{
                          br: <br />,
                          fragment: <></>,
                          span: <span />,
                        }}
                        values={
                          // eslint-disable-next-line no-nested-ternary
                          width <= 376
                            ? {
                                orangeText: `${t(title.orangeText)} `,
                                text: `${t(title.sellYour ?? '')}<br />${t(title.carAtOurBestPrice ?? '')}`,
                              }
                            : width <= 768
                            ? {
                                orangeText: `${t(title.orangeText)} `,
                                text: `${t(title.sellYourCar ?? '')}<br />${t(title.atOurBestPrice ?? '')}`,
                              }
                            : {
                                orangeText: `${t(title.orangeText)}<br />`,
                                text: `${t(title.sellYourCarAt ?? '')}<br />${t(title.ourBestPrice ?? '')}`,
                              }
                        }
                        as="h1"
                      />
                    ) : (
                      <Trans
                        ns="sellCar"
                        i18nKey={titleKey}
                        components={{
                          br: (
                            <>
                              <br />
                              <span> </span>
                            </>
                          ),
                          fragment: <></>,
                          span: <span />,
                        }}
                        values={
                          isNewDesign && width <= 767
                            ? { text: `${t(title.text ?? '')}<br/>${t(title.orangeText)}`, orangeText: '' }
                            : { text: t(title.text ?? ''), orangeText: t(title.orangeText) }
                        }
                      />
                    )}
                  </StyledTitle>
                )}
              </StyledLeftBanner>

              {isAboveTablet && (
                <StyledSellCarForm id="sell-car-form" country={country} {...formInfo}>
                  <SellCarRequestFormWrapperContainer isNewDesign={isNewDesign} />
                </StyledSellCarForm>
              )}
            </StyledRow>
          </StyledContent>
        </StyledContainerWrapper>
      </StyledWrapper>

      {isBelowTablet && (
        <Suspense fallback={<SellCarRequestFormContainerSkeleton country={country} />}>
          <SellCarRequestFormWrapperContainer isNewDesign={isNewDesign} />
        </Suspense>
      )}
    </>
  );
};

SellBanner.defaultProps = {
  country: 'sg',
};

export default SellBanner;
